<script lang="ts">
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import { investmentCalculatorAccessFormClientSchema } from "./investment-calculator-access-form.schema";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { toast } from "svelte-sonner";
	import { formatPhoneNumber } from "#lib/web-ui/helpers.js";
	import { dev } from "$app/env";
	import { TurnstileField } from "./turnstile";
	import { page } from "$app/state";

	let { onSuccess }: { onSuccess: () => void } = $props();

	const form = superForm(defaults(zod4(investmentCalculatorAccessFormClientSchema)), {
		id: "investment-calculator-access",
		SPA: true,
		resetForm: true,
		validators: zod4(investmentCalculatorAccessFormClientSchema),
		async onUpdate({ form }) {
			if (!form.valid) return;

			try {
				await trpcBrowser.public.forms.submitInvestmentCalculatorAccess.mutate({
					...form.data,
					pageUri: page.url.href,
					pageName: document.title
				});
			} catch (e) {
				form.data.turnstileToken = "";
				toast.error(
					e instanceof Error ? e.message : "Failed to submit form. Please try again."
				);
				return;
			}

			onSuccess();
			toast.success("Form submitted successfully!");
		}
	});

	const { form: formData, enhance, errors, submitting } = form;

	if (dev) {
		$formData.firstName = "John";
		$formData.lastName = "Doe";
		$formData.email = "john.doe@example.com";
		$formData.phone = "(123) 456-7890";
	}
</script>

<form use:enhance method="POST" class="w-full space-y-6">
	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
		<div class="space-y-2.5">
			<Label for="ica-firstName">First Name</Label>
			<Input
				id="ica-firstName"
				name="firstName"
				type="text"
				bind:value={$formData.firstName}
				autocomplete="given-name"
			/>
			{#if $errors.firstName}
				<p class="text-sm text-red-500">{$errors.firstName}</p>
			{/if}
		</div>

		<div class="space-y-2.5">
			<Label for="ica-lastName">Last Name</Label>
			<Input
				id="ica-lastName"
				name="lastName"
				type="text"
				bind:value={$formData.lastName}
				autocomplete="family-name"
			/>
			{#if $errors.lastName}
				<p class="text-sm text-red-500">{$errors.lastName}</p>
			{/if}
		</div>
	</div>

	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
		<div class="space-y-2.5">
			<Label for="ica-email">Email</Label>
			<Input
				id="ica-email"
				name="email"
				type="email"
				bind:value={$formData.email}
				autocomplete="email"
			/>
			{#if $errors.email}
				<p class="text-sm text-red-500">{$errors.email}</p>
			{/if}
		</div>

		<div class="space-y-2.5">
			<Label for="ica-phone">Phone</Label>
			<Input
				id="ica-phone"
				name="phone"
				type="tel"
				bind:value={$formData.phone}
				placeholder="(123) 456-7890"
				autocomplete="tel"
				onchange={(e) => {
					$formData.phone = formatPhoneNumber(e.currentTarget.value);
				}}
			/>
			{#if $errors.phone}
				<p class="text-sm text-red-500">{$errors.phone}</p>
			{/if}
		</div>
	</div>

	<TurnstileField bind:token={$formData.turnstileToken} class="mb-4" />

	<Button
		type="submit"
		disabled={$submitting}
		aria-busy={$submitting}
		class="h-10 w-full rounded bg-green-600 text-base font-semibold hover:bg-green-700"
	>
		{$submitting ? "Submitting..." : "Submit"}
	</Button>
</form>
